import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { uploadData } from 'aws-amplify/storage';
import { generateClient } from 'aws-amplify/api';
import { getCurrentUser } from 'aws-amplify/auth';
import { 
    FaCloudUploadAlt, FaLock,  FaRegClock, 
    FaCheckCircle, FaArrowLeft, FaShieldAlt, FaFileSignature 
} from 'react-icons/fa';
// IMPORTANTE: Importamos tu Modal para que se abra aquí mismo
import LoginModal from '../components/LoginModal'; 
// QUIRÚRGICO: Añadida mutación de notificación
import { createDocumentRequest, createNotification } from '../graphql/mutations';
import './FileUploadPage.css';

const FileUploadPage = () => {
    const client = generateClient();
    const navigate = useNavigate();
    
    // ESTADOS ORIGINALES - SE MANTIENEN TODAS LAS FUNCIONES
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [userAuth, setUserAuth] = useState(null);
    const [selectedFile, setSelectedFile] = useState(null);

    // NUEVO ESTADO: Controla que el Modal se abra o se cierre
    const [isModalOpen, setIsModalOpen] = useState(false);
    
    const [formData, setFormData] = useState({
        nombre: '',
        whatsapp: '',
        tipoDocumento: '',
        detalles: ''
    });

    useEffect(() => {
        const checkUser = async () => {
            try {
                const user = await getCurrentUser();
                setUserAuth(user);
            } catch (err) { 
                console.log("Usuario no autenticado, se activa bloqueo visual."); 
            }
        };
        checkUser();
    }, []);

    const handleFileChange = (e) => {
        if (e.target.files[0]) setSelectedFile(e.target.files[0]);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        // Si no está logueado, en lugar de un alert, abrimos el Modal
        if (!userAuth) {
            setIsModalOpen(true);
            return;
        }
        if (!selectedFile) return alert("⚠️ Por favor, adjunte el archivo para que el Dr. pueda revisarlo.");
        
        setUploading(true);
        try {
            // CORRECCIÓN: Limpieza de nombre de archivo para evitar errores en S3
            const fileExtension = selectedFile.name.split('.').pop();
            const cleanName = selectedFile.name
                .replace(`.${fileExtension}`, "")
                .replace(/[^a-zA-Z0-9]/g, "_"); // Cambia espacios y puntos por guiones bajos
            const docKey = `revisiones/${Date.now()}_${cleanName}.${fileExtension}`;

            // Carga de archivo a S3 con clave única
            await uploadData({ 
                path: `public/${docKey}`, 
                data: selectedFile,
                options: {
                    contentType: selectedFile.type
                }
            }).result;

            const userEmail = userAuth.signInDetails?.loginId || userAuth.username;

            // Creación de solicitud en AppSync (usando API KEY)
            await client.graphql({
                query: createDocumentRequest,
                variables: {
                    input: {
                        customerName: formData.nombre,
                        customerEmail: userEmail,
                        phone: formData.whatsapp,
                        areaLegal: formData.tipoDocumento,
                        documentType: formData.tipoDocumento,
                        description: formData.detalles,
                        clientFileKey: docKey,
                        status: "EN_REVISION", 
                        priceUSD: 0.0, 
                        requestDate: new Date().toISOString()
                    }
                },
                authMode: 'apiKey' 
            });

            // INYECCIÓN QUIRÚRGICA: Notificar al Administrador
            await client.graphql({
                query: createNotification,
                variables: {
                    input: {
                        recipientEmail: "giovanniscarvaciyasociados@gmail.com",
                        title: "📂 NUEVA SOLICITUD DE TRÁMITE",
                        message: `El cliente ${formData.nombre} (${userEmail}) ha subido un documento para revisión de ${formData.tipoDocumento}.`,
                        isRead: false
                    }
                },
                authMode: 'apiKey'
            });

            setIsSubmitted(true);
        } catch (error) {
            console.error("Error al enviar solicitud:", error);
            alert("Hubo un error al procesar su solicitud. Verifique que el archivo no sea demasiado pesado.");
        } finally {
            setUploading(false);
        }
    };

    // Pantalla de éxito tras el envío
    if (isSubmitted) {
        return (
            <div className="file-upload-full-page">
                <div className="form-success-message fade-in">
                    <FaCheckCircle size="80px" color="#28a745" />
                    <h2>¡Solicitud Enviada!</h2>
                    <p>El <strong>Dr. Giovanni Scarvaci</strong> ha sido notificado.</p>
                    <p>Una vez analizado su documento, se le asignará un presupuesto que podrá ver y pagar en su <strong>Panel de Usuario</strong>.</p>
                    <button className="cta-button primary-gold" onClick={() => navigate('/profile')}>
                        Ir a Mi Panel de Documentos
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="file-upload-full-page">
            <LoginModal 
                isOpen={isModalOpen} 
                onClose={() => setIsModalOpen(false)} 
            />

            <header className="upload-header">
                <h1 className="main-title">Revisión Profesional de Documentos</h1>
                <h2 className="firm-name">Giovanni Scarvaci y Asociados</h2>
            </header>

            <div className="info-steps-grid">
                <div className="step-card">
                    <FaShieldAlt className="step-icon" />
                    <h3>1. Registro</h3>
                    <p>Acceso exclusivo para usuarios registrados en la plataforma.</p>
                </div>
                <div className="step-card">
                    <FaRegClock className="step-icon" />
                    <h3>2. Evaluación</h3>
                    <p>El Dr. analizará su caso y le enviará el presupuesto a su panel.</p>
                </div>
                <div className="step-card">
                    <FaFileSignature className="step-icon" />
                    <h3>3. Edición Legal</h3>
                    <p>Tras validar su pago, recibirá el documento corregido por expertos.</p>
                </div>
            </div>

            <div className="form-container-wrapper">
                {!userAuth && (
                    <div className="lock-overlay">
                        <FaLock size="50px" color="#D4AF37" />
                        <h3 style={{marginTop: '15px'}}>Inicie Sesión</h3>
                        <p>Debe estar registrado para solicitar una revisión legal.</p>
                        <button 
                            onClick={() => setIsModalOpen(true)} 
                            className="btn-login-redirect"
                        >
                            Entrar / Registrarse
                        </button>
                    </div>
                )}

                <form className={`file-analysis-form ${!userAuth ? 'blur-effect' : ''}`} onSubmit={handleSubmit}>
                    <div className="form-grid">
                        <div className="input-field">
                            <label>Nombre Completo</label>
                            <input type="text" required onChange={(e) => setFormData({...formData, nombre: e.target.value})} disabled={!userAuth} />
                        </div>
                        <div className="input-field">
                            <label>WhatsApp</label>
                            <input type="text" placeholder="+58..." required onChange={(e) => setFormData({...formData, whatsapp: e.target.value})} disabled={!userAuth} />
                        </div>
                        <div className="input-field">
                            <label>Área Legal / Servicio</label>
                            <select required onChange={(e) => setFormData({...formData, tipoDocumento: e.target.value})} disabled={!userAuth}>
                                <option value="">Seleccione...</option>
                                <option value="Derecho Civil">Derecho Civil</option>
                                <option value="Derecho Mercantil">Derecho Mercantil</option>
                                <option value="Contratos">Contratos</option>
                                <option value="Documentos">Documentos</option>
                                <option value="Avaluos">Avalúos</option>
                                <option value="Contratos Inmobiliarios">Contratos Inmobiliarios</option>
                                <option value="Impugnacion de Avaluos">Impugnación de Avalúos</option>
                            </select>
                        </div>
                    </div>
                    <div className="input-field">
                        <label>Observaciones o Instrucciones Específicas</label>
                        <textarea rows="4" placeholder="Indique aquí qué necesita que el Dr. revise o edite específicamente..." onChange={(e) => setFormData({...formData, detalles: e.target.value})} disabled={!userAuth}></textarea>
                    </div>
                    
                    <div className={`file-input-wrapper ${selectedFile ? 'file-selected' : ''}`}>
                        <input type="file" id="file-upload" onChange={handleFileChange} accept=".pdf,.doc,.docx" hidden disabled={!userAuth} />
                        <label htmlFor="file-upload" className="file-name-display">
                            {selectedFile ? <><FaCheckCircle color="#28a745" /> {selectedFile.name}</> : <><FaCloudUploadAlt /> Haga clic para subir Documento (PDF o Word)</>}
                        </label>
                    </div>

                    <button type="submit" className="cta-button primary-gold" disabled={uploading || !userAuth}>
                        {uploading ? "ENVIANDO SOLICITUD..." : "SOLICITAR PRESUPUESTO DE REVISIÓN"}
                    </button>
                </form>
            </div>

            <footer className="upload-footer-nav">
                <button onClick={() => navigate('/')} className="final-back-button">
                    <FaArrowLeft /> Regresar al Inicio
                </button>
            </footer>
        </div>
    );
};

export default FileUploadPage;